import { Injectable, Logger } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { RESERVED_SUBDOMAINS, validateSubdomainFormat } from '@cart/shared-types';

export const TENANT_DOMAIN_SUFFIX = process.env.TENANT_DOMAIN_SUFFIX ?? 'forgestack.in';

/**
 * The GST slabs every Indian store starts with. A store without these cannot
 * price anything correctly, so they are part of "a usable store", not an
 * optional extra.
 */
export const DEFAULT_TAX_SLABS = [
  { code: 'GST0', name: 'GST 0%', hsnGroup: 'exempt', igst: 0, cgst: 0, sgst: 0 },
  { code: 'GST5', name: 'GST 5%', hsnGroup: 'essential', igst: 500, cgst: 250, sgst: 250 },
  { code: 'GST12', name: 'GST 12%', hsnGroup: 'standard-low', igst: 1200, cgst: 600, sgst: 600 },
  { code: 'GST18', name: 'GST 18%', hsnGroup: 'standard', igst: 1800, cgst: 900, sgst: 900 },
  { code: 'GST28', name: 'GST 28%', hsnGroup: 'luxury', igst: 2800, cgst: 1400, sgst: 1400 },
];

export interface ProvisionStoreInput {
  organizationId: string;
  /** The user who will own it — gets the active StoreStaff row. */
  userId: string;
  name: string;
  subdomain: string;
  slug: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  currency?: string | null;
  timezone?: string | null;
  createdBy?: string;
}

/**
 * Creates a store with everything it needs to actually be used.
 *
 * "A store" is not one row. Without its domain it is unreachable, without tax
 * slabs it cannot price anything, and without a StoreStaff row its own owner is
 * refused by AdminAccessGuard on every admin screen. Those pieces were spread
 * across two call sites and one of them created only the bare row, which is how
 * merchants ended up locked out of stores they owned.
 *
 * One definition, in one transaction, used by both the self-serve flow and
 * registration. Deliberately dependency-free apart from Prisma so it can be
 * provided to AuthModule and MerchantModule alike — MerchantModule already
 * imports AuthModule, so the dependency cannot run the other way.
 */
@Injectable()
export class StoreProvisioningService {
  private readonly logger = new Logger(StoreProvisioningService.name);

  constructor(private readonly prisma: PrismaService) {}

  /**
   * Turns a business name into a usable subdomain, and keeps trying until it
   * finds a free one.
   *
   * Registration does not ask for a subdomain, so one has to be derived. A
   * collision here must never fail a signup — the merchant can rename the store
   * later, but they cannot recover from having no store at all.
   */
  async deriveSubdomain(businessName: string): Promise<string | null> {
    const base = businessName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);

    const seed = base.length >= 3 ? base : `shop-${base}`.slice(0, 40);

    for (let attempt = 0; attempt < 25; attempt += 1) {
      const candidate = attempt === 0 ? seed : `${seed}-${attempt + 1}`.slice(0, 63);

      if (validateSubdomainFormat(candidate)) continue;
      if (RESERVED_SUBDOMAINS.includes(candidate)) continue;

      const taken = await this.prisma.storeDomain.findUnique({
        where: { domain: `${candidate}.${TENANT_DOMAIN_SUFFIX}` },
        select: { id: true },
      });

      if (!taken) return candidate;
    }

    return null;
  }

  /** Unique within the organisation, which is the scope the slug is unique in. */
  async uniqueSlug(name: string, organizationId: string): Promise<string> {
    const base =
      name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'store';

    for (let attempt = 0; attempt < 50; attempt += 1) {
      const candidate = attempt === 0 ? base : `${base}-${attempt + 1}`;
      const taken = await this.prisma.store.findFirst({
        where: { organizationId, slug: candidate },
        select: { id: true },
      });

      if (!taken) return candidate;
    }

    return `${base}-${Date.now().toString(36)}`;
  }

  /**
   * Everything in ONE transaction. A partially-provisioned store is worse than
   * no store: it exists, it looks live, and its owner cannot administer it.
   */
  async provision(input: ProvisionStoreInput, tx?: Prisma.TransactionClient) {
    const run = async (client: Prisma.TransactionClient) => {
      const actor = input.createdBy ?? input.userId;

      const store = await client.store.create({
        data: {
          organizationId: input.organizationId,
          name: input.name,
          slug: input.slug,
          city: input.city ?? null,
          state: input.state ?? null,
          country: input.country ?? 'IN',
          defaultCurrency: input.currency ?? 'INR',
          timezone: input.timezone ?? 'Asia/Kolkata',
          defaultLocale: 'en-IN',
          isActive: true,
          createdBy: actor,
          updatedBy: actor,
        },
      });

      // Live the moment this commits — the wildcard DNS already covers it, so
      // there is no external provisioning step (plan.md §3.4).
      await client.storeDomain.create({
        data: {
          storeId: store.id,
          domain: `${input.subdomain}.${TENANT_DOMAIN_SUFFIX}`,
          isPrimary: true,
          verificationStatus: 'verified',
          createdBy: actor,
          updatedBy: actor,
        },
      });

      await client.taxSlabProfile.createMany({
        data: DEFAULT_TAX_SLABS.map((slab) => ({
          storeId: store.id,
          code: slab.code,
          name: slab.name,
          hsnGroup: slab.hsnGroup,
          igstRateBps: slab.igst,
          cgstRateBps: slab.cgst,
          sgstRateBps: slab.sgst,
          createdBy: actor,
          updatedBy: actor,
        })),
      });

      // The owner's staff row. AdminAccessGuard grants access on this and
      // nothing else, so a store without it locks its own owner out of every
      // admin screen with "Admin access is required".
      await client.storeStaff.upsert({
        where: { storeId_userId: { storeId: store.id, userId: input.userId } },
        update: { isActive: true, role: 'merchant', deletedAt: null, updatedBy: actor },
        create: {
          storeId: store.id,
          userId: input.userId,
          organizationId: input.organizationId,
          role: 'merchant',
          isActive: true,
          joinedAt: new Date(),
          createdBy: actor,
          updatedBy: actor,
        },
      });

      // Point the owner's `User.storeId` at this store when it is still the
      // schema placeholder ('store_demo').
      //
      // The admin frontend reads that column to choose the active store and
      // sends it as `x-store-id`. Left at the placeholder it makes the merchant
      // claim the demo store on every request, which they hold no StoreStaff
      // row for — so the API refuses all of them and the whole admin reads as
      // "Admin access is required". Nothing set this before; a store was
      // created and the pointer was left dangling.
      //
      // Only overwritten when the current value is not a store they can
      // actually administer, so a merchant's deliberate choice of active store
      // is never clobbered by creating another one.
      const owner = await client.user.findUnique({
        where: { id: input.userId },
        select: { storeId: true },
      });

      const usable = owner?.storeId
        ? await client.storeStaff.findFirst({
            where: { userId: input.userId, storeId: owner.storeId, isActive: true },
            select: { id: true },
          })
        : null;

      if (!usable) {
        await client.user.update({
          where: { id: input.userId },
          data: { storeId: store.id },
        });
      }

      return store;
    };

    // Email templates are NOT copied here — a store with no template rows
    // inherits the system defaults on TemplateType (migration 052).
    const store = tx ? await run(tx) : await this.prisma.$transaction(run);

    this.logger.log(`provisioned store "${store.name}" (${store.id}) for user ${input.userId}`);
    return { ...store, domain: `${input.subdomain}.${TENANT_DOMAIN_SUFFIX}` };
  }
}
